import React, { FC, useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import Clear from '@crehana/crehana-icons/lib/Clear';
import { DialogProps } from './types';
import { Container, Row, Col } from '../GridV2';
import { baseLighter } from '../styles/colors';
import { DIALOG_SHEET_COL_PROPS } from './constants';

const Dialog: FC<DialogProps> = ({
  size = 'm',
  open = false,
  onDismiss,
  onEscape,
  hideCloseBtn = false,
  ignoreEscape = false,
  ignoreBackdropClick = false,
  hideBackdrop = false,
  hideDialogSheet = false,
  rootClassName = '',
  className = '',
  backdropClassName = '',
  disabledScrollLock,
  disabledFocusLock,
  children,
  containerHTMLProps = {},
  dark,
  datatestid,
  'data-testid': dataTestId,
  ...other
}) => {
  const [isClient, setIsClient] = useState(false);
  const dialogSheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const clickOutsideListener = (
      event:
        | HTMLElementEventMap['mousedown']
        | HTMLElementEventMap['touchstart'],
    ) => {
      if (
        dialogSheetRef.current &&
        !dialogSheetRef.current.contains(event.target as Node) &&
        onDismiss
      ) {
        onDismiss();
      }
    };

    if (open && !ignoreBackdropClick && size !== 'full') {
      document.addEventListener('mousedown', clickOutsideListener);
      document.addEventListener('touchstart', clickOutsideListener);
    } else {
      document.removeEventListener('mousedown', clickOutsideListener);
      document.removeEventListener('touchstart', clickOutsideListener);
    }

    return () => {
      document.removeEventListener('mousedown', clickOutsideListener);
      document.removeEventListener('touchstart', clickOutsideListener);
    };
  }, [ignoreBackdropClick, onDismiss, open, size]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.keyCode === 27) {
      event.stopPropagation();
      if (typeof onEscape === 'function') {
        onEscape(event);
      }
      if (open && !ignoreEscape && typeof onDismiss === 'function') {
        onDismiss();
      }
    }
  };

  // SSR support
  if (!isClient || typeof document === 'undefined') {
    return null;
  }

  const renderCloseButton = () => {
    let buttonClasses = 'mr-20 mt-40 sm:mr-24 sm:mt-24';
    let iconClasses = 'w-24 h-24 sm:w-32 sm:h-32';

    if (size === 's') {
      buttonClasses = 'mr-8 mt-8 sm:mr-16 sm:mt-16';
      iconClasses = 'w-24 h-24';
    }
    if (size === 'full') {
      buttonClasses = 'mr-20 mt-32 sm:mr-32';
    }
    return (
      <button
        type="button"
        className={`focus:outline-none absolute top-0 right-0 z-1 ${buttonClasses}`}
        onClick={onDismiss}
      >
        <Clear className={iconClasses} color={baseLighter} />
      </button>
    );
  };

  const renderChildren = () => (open ? children : null);

  return ReactDOM.createPortal(
    <div
      className={cn(
        'fixed inset-0 z-dialog',
        open ? 'cui-is-visible' : 'cui-is-hidden',
        rootClassName,
      )}
      role="dialog"
      {...containerHTMLProps}
    >
      {!hideBackdrop && (
        <div
          className={cn(
            'fixed inset-0 bg-base-main opacity-80 -z-1',
            backdropClassName,
          )}
          tabIndex={-1}
        />
      )}
      <FocusLock
        disabled={disabledFocusLock || open}
        returnFocus
        className="h-full"
      >
        <RemoveScroll enabled={!disabledScrollLock && open} forwardProps>
          <div className="w-full h-full">
            {hideDialogSheet ? (
              renderChildren()
            ) : (
              <Container
                className="h-full items-center z-1 focus:outline-none"
                full={size === 'full' || { xs: size !== 's', sm: false }}
                ignoreKeyboardDebug={size === 'full'}
                role="document"
                tabIndex={-1}
                onKeyDown={handleKeyDown}
                data-testid={dataTestId || datatestid}
              >
                <Row className="h-full items-center" nowrap>
                  <Col {...DIALOG_SHEET_COL_PROPS[size]}>
                    <div
                      ref={dialogSheetRef}
                      className={cn(
                        'relative overflow-y-auto overflow-x-hidden',
                        dark
                          ? 'bg-base-dark text-white'
                          : 'bg-white text-base-main',
                        size === 's'
                          ? 'max-h-90vh'
                          : 'h-full min-h-screen max-h-screen',
                        {
                          'sm:h-auto sm:min-h-0 sm:max-h-90vh': size !== 'full',
                        },
                        className,
                      )}
                      {...other}
                    >
                      {!hideCloseBtn && renderCloseButton()}
                      {renderChildren()}
                    </div>
                  </Col>
                </Row>
              </Container>
            )}
          </div>
        </RemoveScroll>
      </FocusLock>
    </div>,
    document.body,
  );
};

export default Dialog;
